/* Second Bloom — swipeable hero: dots, auto-advance, videos, and nothing that gets in the way of the swipe. */
(function () {
  if (customElements.get('sb-hero')) return;

  var SLOW = window.matchMedia('(prefers-reduced-motion: reduce)');
  var WIDE = window.matchMedia('(min-width: 750px)');
  var SAVE_DATA = !!(navigator.connection && navigator.connection.saveData);

  class SBHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sbh-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-sbh-slide]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-sbh-dot]'));
      if (!this.track || !this.slides.length) return;

      this.index = 0;
      this.every = parseInt(this.dataset.every || '0', 10) * 1000;
      this.waitVideo = this.dataset.waitVideo === '1';
      this.paused = false;
      this.ratios = this.slides.map(function () { return 0; });

      this.setupVideos();
      if (this.slides.length < 2) return;

      this.onScroll = this.throttle(() => this.readIndex());
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // A finger on the track means the shopper is in charge; stop advancing under them.
      ['pointerdown', 'touchstart', 'wheel'].forEach((n) => {
        this.track.addEventListener(n, () => this.hold(), { passive: true });
      });
      // Pause under the cursor, but only where there is a real one: a tap on a phone can fire
      // mouseenter with no mouseleave to follow, which would stop the slider for good.
      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => { this.paused = true; });
        this.addEventListener('mouseleave', () => { this.paused = false; });
      }
      this.onVisibility = () => {
        if (document.hidden) { this.stop(); this.pauseAll(); }
        else { this.start(); this.syncVideos(); }
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.dots.forEach((d, i) => {
        d.addEventListener('click', () => {
          this.hold();
          this.go(i);
        });
      });

      // Keyboard, for anyone tabbing through.
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.hold(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.hold(); this.go(this.index - 1); }
      });

      // In the theme editor, picking a slide in the sidebar brings it into view.
      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (e) => {
          var i = this.slides.indexOf(e.target);
          if (i > -1) { this.hold(); this.go(i); }
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.readIndex();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.resume);
      if (this.io) this.io.disconnect();
      this.disarmGestureRetry();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onWide) WIDE.removeEventListener && WIDE.removeEventListener('change', this.onWide);
    }

    throttle(fn) {
      var waiting = false;
      return () => {
        if (waiting) return;
        waiting = true;
        requestAnimationFrame(() => { waiting = false; fn(); });
      };
    }

    // ---- Videos -------------------------------------------------------------------------
    // A slide's video downloads only once a good part of that slide is on screen, plays only while
    // most of it is, and pauses the moment it is swiped or scrolled away. Peeking neighbours never load.

    setupVideos() {
      this.hasVideo = !!this.querySelector('[data-sbh-video]');
      if (!this.hasVideo) return;

      this.querySelectorAll('[data-sbh-video]').forEach((v) => {
        v.muted = true;
        v.defaultMuted = true;
        v.addEventListener('playing', () => {
          v.classList.add('is-on');
          delete v.dataset.blocked;
          this.dropImgFallback(v);
          if (!this.querySelector('[data-sbh-video][data-blocked]') && this.allUnlockedOrPlayed()) this.disarmGestureRetry();
        });
        v.addEventListener('ended', () => this.onVideoEnded(v));
        // Browsers report an error for a source with no address yet, which is every video until its
        // slide comes on screen. Only an error after the real address is set means it is broken.
        v.addEventListener('error', () => { if (v.dataset.loaded) v.dataset.failed = '1'; }, true);
      });

      if (!('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          var i = this.slides.indexOf(e.target);
          if (i > -1) this.ratios[i] = e.isIntersecting ? e.intersectionRatio : 0;
        });
        this.syncVideos();
      }, { threshold: [0, 0.3, 0.6, 0.9] });
      this.slides.forEach((s) => this.io.observe(s));

      // Turning a tablet or resizing across the phone/desktop line swaps which video shows.
      this.onWide = () => { this.pauseAll(); this.syncVideos(); };
      if (WIDE.addEventListener) WIDE.addEventListener('change', this.onWide);
    }

    // The video that is actually showing in a slide at this screen size, if any.
    videoIn(slide) {
      var vids = slide.querySelectorAll('[data-sbh-video]');
      for (var i = 0; i < vids.length; i++) {
        if (getComputedStyle(vids[i]).display !== 'none') return vids[i];
      }
      return null;
    }

    load(v) {
      if (v.dataset.loaded) return;
      var src = v.querySelector('source[data-src]');
      if (!src) return;
      src.src = src.dataset.src;
      v.dataset.loaded = '1';
      v.preload = 'auto';
      v.load();
    }

    syncVideos() {
      if (!this.hasVideo) return;
      // Reduce Motion only stops the slider moving on its own (see start()). The videos are short,
      // silent and gentle, so they still play for those shoppers instead of freezing on the first
      // frame. Data Saver is the one setting that keeps them still, to save the shopper's data.
      var still = SAVE_DATA;
      this.slides.forEach((s, i) => {
        var v = this.videoIn(s);
        if (!v || v.dataset.failed || v.dataset.imgFallback) return;
        var r = this.ratios[i];
        if (still || document.hidden) { if (!v.paused) v.pause(); return; }
        if (r >= 0.3) this.load(v);
        if (r >= 0.6) {
          var p = v.play();
          if (p && p.catch) p.catch(() => {
            v.dataset.blocked = '1';
            this.armGestureRetry();
            if (this.canImgVideo()) this.imgFallback(v);
          });
        } else if (!v.paused) {
          v.pause();
        }
      });
    }

    // iPhones in Low Power Mode (and some Android battery savers) refuse to start any video on their
    // own, even silent ones, and no site can override that. They do allow it the moment the shopper
    // touches the page, and a video started from a touch is free to play from then on. So on the next
    // tap, swipe or key press every hero video for this screen size is started for an instant, which
    // unlocks it, and the ones not on screen are paused again straight away. Until then each slide
    // shows its photo, or the video's first frame, so nothing looks broken.
    armGestureRetry() {
      if (this.onGesture) return;
      this.onGesture = () => {
        var pending = [];
        this.querySelectorAll('[data-sbh-video]').forEach((v) => {
          if (v.dataset.failed || v.dataset.unlocked) return;
          if (getComputedStyle(v).display === 'none') return;
          delete v.dataset.blocked;
          this.load(v);
          var p = v.play();
          if (p && p.then) {
            pending.push(p.then(() => { v.dataset.unlocked = '1'; }, () => { v.dataset.blocked = '1'; }));
          }
        });
        // Then back to normal: only what is actually on screen keeps playing.
        Promise.all(pending).then(() => this.syncVideos());
      };
      ['touchend', 'click', 'keydown'].forEach((n) => {
        document.addEventListener(n, this.onGesture, { capture: true, passive: true });
      });
    }

    // Safari (and every browser on iPhone and iPad, which all run on Safari's engine) can show an MP4
    // through an image tag, where it plays like a silent, looping GIF. Low Power Mode may leave that
    // alone even when it refuses the video, so a refused video is swapped for that stand-in, using the
    // same file. Other browsers can't do this and never try. If the stand-in fails to load it simply
    // removes itself, and the slide keeps its photo until the first touch starts the real video.
    canImgVideo() {
      var ua = navigator.userAgent || '';
      return /AppleWebKit/.test(ua) && !/(Chrome|Chromium|Edg|OPR|Android)\//.test(ua);
    }

    imgFallback(v) {
      if (v.sbhImg) return;
      var src = v.querySelector('source');
      var url = src && (src.getAttribute('src') || src.dataset.src);
      if (!url) return;
      var img = document.createElement('img');
      // Same size classes as the video, so the phone and desktop versions still swap correctly.
      img.className = 'sbh__vid sbh__vid--img' +
        (v.classList.contains('sbh__media--m') ? ' sbh__media--m' : '') +
        (v.classList.contains('sbh__media--d') ? ' sbh__media--d' : '');
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.decoding = 'async';
      img.addEventListener('load', () => img.classList.add('is-on'));
      img.addEventListener('error', () => this.dropImgFallback(v));
      v.sbhImg = img;
      v.dataset.imgFallback = '1';
      v.insertAdjacentElement('afterend', img);
      img.src = url;
    }

    dropImgFallback(v) {
      if (!v.sbhImg) return;
      v.sbhImg.remove();
      v.sbhImg = null;
      delete v.dataset.imgFallback;
    }

    // True once every video for this screen size has either played or been unlocked by a touch.
    allUnlockedOrPlayed() {
      var vids = this.querySelectorAll('[data-sbh-video]');
      for (var i = 0; i < vids.length; i++) {
        var v = vids[i];
        if (getComputedStyle(v).display === 'none' || v.dataset.failed) continue;
        if (!v.dataset.unlocked && !v.classList.contains('is-on')) return false;
      }
      return true;
    }

    disarmGestureRetry() {
      if (!this.onGesture) return;
      ['touchend', 'click', 'keydown'].forEach((n) => {
        document.removeEventListener(n, this.onGesture, { capture: true });
      });
      this.onGesture = null;
    }

    pauseAll() {
      this.querySelectorAll('[data-sbh-video]').forEach((v) => { if (!v.paused) v.pause(); });
    }

    // With "play to the end" on, the end of the video is what moves the slider along.
    // Otherwise, or once the shopper has taken over, it simply starts again.
    onVideoEnded(v) {
      var slide = v.closest('[data-sbh-slide]');
      var isCurrent = this.slides.indexOf(slide) === this.index;
      if (this.waitVideo && isCurrent && this.timer && !this.paused && this.slides.length > 1) {
        this.go(this.index + 1);
        this.restartTimer();
        return;
      }
      v.currentTime = 0;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    // True while the current slide's video is running and should be left to finish.
    videoHoldsCurrent() {
      if (!this.waitVideo || !this.hasVideo) return false;
      var v = this.videoIn(this.slides[this.index]);
      return !!(v && v.dataset.loaded && !v.dataset.failed && !v.dataset.blocked && !v.paused);
    }

    // ---- Slider -------------------------------------------------------------------------

    // Which slide is nearest the middle of the hero right now.
    readIndex() {
      var mid = this.track.scrollLeft + this.track.clientWidth / 2;
      var best = 0;
      var bestGap = Infinity;
      this.slides.forEach(function (s, i) {
        var centre = s.offsetLeft + s.offsetWidth / 2;
        var gap = Math.abs(centre - mid);
        if (gap < bestGap) { bestGap = gap; best = i; }
      });
      if (best === this.index && this.dotsDone) return;
      this.index = best;
      this.dotsDone = true;
      this.dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === best ? 'true' : 'false');
      });
      this.slides.forEach(function (s, i) {
        // Keep the off-screen slides out of the tab order and off the screen reader.
        s.setAttribute('aria-hidden', i === best ? 'false' : 'true');
        if (s.tagName === 'A') s.tabIndex = i === best ? 0 : -1;
      });
      this.syncVideos();
    }

    go(i) {
      var n = this.slides.length;
      var target = ((i % n) + n) % n;
      var s = this.slides[target];
      if (!s) return;
      var left = s.offsetLeft - (this.track.clientWidth - s.offsetWidth) / 2;
      // Only the track scrolls sideways. Never the page, so nothing above or below ever moves.
      this.track.scrollTo({ left: left, behavior: SLOW.matches ? 'auto' : 'smooth' });
    }

    // After a swipe, leave the shopper alone for a while before advancing again.
    hold() {
      this.stop();
      clearTimeout(this.resume);
      this.resume = setTimeout(() => this.start(), Math.max(this.every, 6000));
    }

    start() {
      this.stop();
      if (!this.every || SLOW.matches || this.slides.length < 2) return;
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        if (this.videoHoldsCurrent()) return;
        // Don't move while the hero is scrolled out of view; nobody would see it.
        if (this.hasVideo && this.io && this.ratios[this.index] === 0) return;
        this.go(this.index + 1);
      }, this.every);
    }

    restartTimer() { if (this.timer) this.start(); }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('sb-hero', SBHero);
})();
